import React, { useEffect, useState } from "react";
import { Sparkles, TrendingUp, ShieldAlert, Target, Loader2, Trophy } from "lucide-react";
import { useI18n } from "../i18n";
import { getWeeklyInsight } from "../lib/api";

// Curated AI "Decision of the Week" card. Cached server-side per ISO week.
export default function WeeklyDecision() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    getWeeklyInsight(lang)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch(() => { if (alive) { setError(true); setLoading(false); } });
    return () => { alive = false; };
  }, [lang]);

  if (error) return null;

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10" data-testid="weekly-decision-section">
      <div
        className="relative overflow-hidden rounded-2xl border border-gold-500/25 bg-gradient-to-br from-gold-500/[0.08] via-black to-black gold-sweep-host"
        data-testid="weekly-decision-card"
      >
        {/* Gold radial glow */}
        <div
          aria-hidden
          className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #E9B934 0%, transparent 60%)" }}
        />
        <div className="relative p-7 sm:p-10 grid lg:grid-cols-12 gap-8">
          {/* LEFT: Title + thesis */}
          <div className="lg:col-span-7 flex flex-col">
            <div className="inline-flex items-center gap-2 self-start rounded-full border border-gold-500/40 bg-gold-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-gold-300 font-mono">
              <Sparkles size={11} />
              {t.weekly?.eyebrow || "Decision of the Week"}
            </div>

            {loading ? (
              <div className="mt-6 flex items-center gap-3 text-neutral-400" data-testid="weekly-loading">
                <Loader2 size={18} className="animate-spin text-gold-400" />
                <span className="font-mono text-sm">{t.weekly?.loading || "Loading…"}</span>
              </div>
            ) : (
              <>
                <h2
                  className="mt-5 font-display text-3xl sm:text-4xl lg:text-[42px] leading-[1.05] tracking-tight text-white"
                  data-testid="weekly-headline"
                >
                  {data.headline}
                </h2>
                {data.subtitle && (
                  <p className="mt-3 text-neutral-400 text-base leading-relaxed max-w-2xl">
                    {data.subtitle}
                  </p>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">
                  <span className="px-2.5 py-1 rounded border border-white/10 bg-white/[0.03] text-gold-300">
                    {data.asset_a_label}
                  </span>
                  <span className="text-neutral-600">{t.weekly?.vs || "vs"}</span>
                  <span className="px-2.5 py-1 rounded border border-white/10 bg-white/[0.03] text-white">
                    {data.asset_b_label}
                  </span>
                  <span className="ml-2 text-neutral-600">·</span>
                  <span className="text-neutral-500">
                    {t.weekly?.horizon || "Horizon"}: {data.horizon_years} {t.weekly?.years || "y"}
                  </span>
                </div>

                {data.thesis && (
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold-400 font-mono flex items-center gap-1.5">
                      <Target size={11} /> {t.weekly?.thesis || "Thesis"}
                    </div>
                    <p className="mt-2 text-neutral-200 leading-relaxed text-[15px]">{data.thesis}</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT: metrics + verdict */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            {!loading && data && (
              <>
                {/* Metrics */}
                {Array.isArray(data.metrics) && data.metrics.length > 0 && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="weekly-metrics">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-mono flex items-center gap-1.5 mb-3">
                      <TrendingUp size={11} /> {t.weekly?.metrics || "Key metrics"}
                    </div>
                    <div className="space-y-3">
                      {data.metrics.map((m, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-baseline border-b border-white/5 last:border-0 pb-2 last:pb-0">
                          <div className="col-span-5 text-xs text-neutral-400">{m.label}</div>
                          <div className="col-span-3 text-sm text-gold-300 font-mono text-right">{m.a}</div>
                          <div className="col-span-1 text-center text-neutral-700 text-xs">/</div>
                          <div className="col-span-3 text-sm text-white font-mono text-right">{m.b}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Verdict */}
                <div className="rounded-xl border border-gold-500/30 bg-gold-500/[0.06] p-5" data-testid="weekly-verdict">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-gold-300 font-mono flex items-center gap-1.5">
                        <Trophy size={11} /> {t.weekly?.winner || "Winner"}
                      </div>
                      <div className="mt-1.5 font-display text-xl text-white truncate">
                        {data.winner === "a"
                          ? data.asset_a_label
                          : data.winner === "b"
                          ? data.asset_b_label
                          : (t.weekly?.tie || "Technical tie")}
                      </div>
                      {data.winner_reason && (
                        <p className="mt-2 text-sm text-neutral-300 leading-snug">{data.winner_reason}</p>
                      )}
                    </div>
                    <ConfidenceRing pct={data.confidence_pct || 70} label={t.weekly?.confidence || "Confidence"} />
                  </div>
                </div>

                {/* Risks + takeaway */}
                {Array.isArray(data.risks) && data.risks.length > 0 && (
                  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-mono flex items-center gap-1.5 mb-2">
                      <ShieldAlert size={11} /> {t.weekly?.risks || "Risks"}
                    </div>
                    <ul className="space-y-1.5">
                      {data.risks.map((r, i) => (
                        <li key={i} className="text-sm text-neutral-300 leading-snug pl-3 relative before:absolute before:left-0 before:top-2 before:w-1 before:h-1 before:rounded-full before:bg-gold-400">
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {data.takeaway && (
                  <div
                    className="rounded-xl border-l-2 border-gold-500 bg-white/[0.02] px-5 py-4"
                    data-testid="weekly-takeaway"
                  >
                    <div className="text-[10px] uppercase tracking-[0.22em] text-gold-300 font-mono">
                      {t.weekly?.takeaway || "Takeaway"}
                    </div>
                    <p className="mt-1.5 text-[15px] text-white leading-relaxed font-display">{data.takeaway}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        {!loading && data && (
          <div className="relative border-t border-white/5 px-7 sm:px-10 py-3 flex flex-wrap items-center justify-between gap-3 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-600">
            <span>{t.weekly?.refreshes || "Refreshed every Monday"} · {data.week_id}</span>
            <span className="truncate max-w-[60%] text-right text-neutral-700">
              {t.weekly?.sources || "Sources"}: Yahoo Finance · CoinGecko · GOLDORA Dataset
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function ConfidenceRing({ pct, label }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
  return (
    <div className="flex flex-col items-center justify-center shrink-0" data-testid="weekly-confidence">
      <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
        <circle cx="28" cy="28" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
        <circle
          cx="28" cy="28" r={r}
          stroke="#E9B934" strokeWidth="4" fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 600ms ease-out" }}
        />
      </svg>
      <div className="-mt-9 text-sm font-mono text-gold-300 font-semibold">{pct}%</div>
      <div className="mt-3 text-[9px] uppercase tracking-[0.18em] text-neutral-500 font-mono">{label}</div>
    </div>
  );
}
